// ============================================================================
// NPSDBusinessConnections.tsx - Connections management component for Business
// Converted from Vue 2 Vue-A2011-NPSDBusinessConnections.ts
//
// Handles Business-to-Client, Business-to-Person (with roles),
// and Business-to-Vehicle connections.
// Uses shared NPSDConnectionPanel component for each panel.
// ============================================================================
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { NPSDConnectionPanel } from '@/components/npsd/NPSDConnectionPanel';
import { removeFromArrayById } from '@/utils/arrays';

interface DeleteConfirmState {
  open: boolean;
  item: any;
  connectionType: string;
  name: string;
}

interface NPSDBusinessConnectionsProps {
  business: any;
  isEditMode?: boolean;
  showAddButton?: boolean;
  onAddConnection?: (type: string) => void;
  onViewConnection?: (type: string, item: any) => void;
  onConnectionRemoved?: (type: string, item: any) => void;
}

export function NPSDBusinessConnections({
  business,
  isEditMode = false,
  showAddButton = false,
  onAddConnection,
  onViewConnection,
  onConnectionRemoved,
}: NPSDBusinessConnectionsProps) {
  const { theme } = useTheme();

  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    item: null,
    connectionType: '',
    name: '',
  });

  // Computed: connection arrays
  const people = useMemo(
    () => business?.connections?.people || [],
    [business?.connections?.people]
  );

  const vehicles = useMemo(
    () => business?.connections?.vehicles || [],
    [business?.connections?.vehicles]
  );

  const clients = useMemo(
    () => business?.connections?.clients || [],
    [business?.connections?.clients]
  );

  // Disable links when on case report tab
  const disableLinks = useMemo(
    () => window.location.href.indexOf('tab=CaseReports') > -1,
    []
  );

  // Add connection - callback to parent
  const handleAddConnection = useCallback(
    (connectionType: string) => {
      onAddConnection?.(connectionType);
    },
    [onAddConnection]
  );

  // View item - callback to parent
  const handleViewItem = useCallback(
    (connectionType: string, item: any) => {
      if (disableLinks) return;
      onViewConnection?.(connectionType, item);
    },
    [disableLinks, onViewConnection]
  );

  // Remove connection - open confirmation
  const handleRemoveConnection = useCallback(
    (connectionType: string, item: any) => {
      let name = '';
      switch (connectionType) {
        case 'person':
          name = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'this person';
          break;
        case 'vehicle':
          name = item.plateNumber || 'this vehicle';
          break;
        case 'client':
          name = item.name || 'this client';
          break;
      }

      setDeleteConfirm({
        open: true,
        item: item,
        connectionType: connectionType,
        name: name,
      });
    },
    []
  );

  // Confirm remove
  const doRemoveConnection = useCallback(() => {
    if (!deleteConfirm.item) {
      setDeleteConfirm({ open: false, item: null, connectionType: '', name: '' });
      return;
    }

    const { item, connectionType } = deleteConfirm;

    // Remove from connections arrays (mutating the source arrays like the Vue version)
    if (connectionType === 'person' && business.connections) {
      removeFromArrayById(business.connections.people, item.id);
      removeFromArrayById(business.connectionsNew?.people, item.id);
    } else if (connectionType === 'vehicle' && business.connections) {
      removeFromArrayById(business.connections.vehicles, item.id);
      removeFromArrayById(business.connectionsNew?.vehicles, item.id);
    } else if (connectionType === 'client' && business.connections) {
      removeFromArrayById(business.connections.clients, item.id);
      removeFromArrayById(business.connectionsNew?.clients, item.id);
    }

    onConnectionRemoved?.(connectionType, item);
    setDeleteConfirm({ open: false, item: null, connectionType: '', name: '' });
  }, [deleteConfirm, business, onConnectionRemoved]);

  // Cancel remove
  const cancelRemove = useCallback(() => {
    setDeleteConfirm({ open: false, item: null, connectionType: '', name: '' });
  }, []);

  return (
    <div className={`npsd-business-connections${theme ? ' ' + theme : ''}`}>
      {/* Client Connections */}
      <NPSDConnectionPanel
        connections={clients}
        connectionType="client"
        title="Client Connections"
        isEditMode={showAddButton}
        disableLinks={disableLinks}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Person Connections */}
      <NPSDConnectionPanel
        connections={people}
        connectionType="person"
        title="Person Connections"
        isEditMode={showAddButton}
        showRoles={true}
        disableLinks={disableLinks}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Vehicle Connections */}
      <NPSDConnectionPanel
        connections={vehicles}
        connectionType="vehicle"
        title="Vehicle Connections"
        isEditMode={showAddButton}
        disableLinks={disableLinks}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Remove Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={`Remove ${deleteConfirm.name}?`}
        message="Are you sure you want to remove this connection?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={doRemoveConnection}
        onCancel={cancelRemove}
      />
    </div>
  );
}

(window as any).NPSDBusinessConnectionsReact = NPSDBusinessConnections;
